import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, Save, RefreshCw, X, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * One-input Quick Add for the authoritative Routing Context.
 * Paste/describe → Analyze → editable 5-field preview → Save.
 *
 * `kind`      : "entity" | "project"
 * `record`    : the current entity/project doc (must include routing_context)
 * `api`       : same shape as RoutingContextEditor (setRoutingContext,
 *              extractRoutingContext)
 * `onSaved`   : callback after save persists
 */
const FIELDS = [
  { key: "purpose", label: "Purpose", rows: 2 },
  { key: "scope", label: "Scope (project)", rows: 3, projectOnly: true },
  { key: "relevant_signals", label: "Relevant signals", rows: 4 },
  { key: "exclusions", label: "Exclusions / out-of-scope", rows: 3 },
  { key: "current_priorities", label: "Current priorities / objective", rows: 3 },
  { key: "interpretation_rules", label: "Interpretation rules", rows: 3 },
];

export default function RoutingContextQuickAdd({ kind, record, api, onSaved }) {
  const existing = record?.routing_context || null;
  const [inputText, setInputText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [proposal, setProposal] = useState(null);          // {purpose, ...}
  const [values, setValues] = useState(null);              // editable copy
  const [saving, setSaving] = useState(false);

  const fields = useMemo(
    () => FIELDS.filter(f => !f.projectOnly || kind === "project"),
    [kind],
  );

  const analyze = async () => {
    if (!inputText.trim()) return toast.error("Describe the context first");
    setAnalyzing(true);
    try {
      const mode = existing ? "update" : "create";
      const r = await api.extractRoutingContext(record.id, inputText.trim(), mode);
      const p = r.data.proposed || {};
      setProposal(p);
      setValues(Object.fromEntries(fields.map(f => [f.key, p[f.key] || ""])));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyze failed");
    } finally { setAnalyzing(false); }
  };

  const regenerate = () => { setProposal(null); setValues(null); };

  const save = async () => {
    if (!values) return;
    const anyFilled = fields.some(f => (values[f.key] || "").trim());
    if (!anyFilled) return toast.error("Preview is empty — add or regenerate");
    setSaving(true);
    try {
      const payload = Object.fromEntries(fields.map(f => [f.key, values[f.key] || ""]));
      await api.setRoutingContext(record.id, payload);
      toast.success(existing ? "Routing Context updated" : "Routing Context saved");
      setInputText("");
      setProposal(null);
      setValues(null);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // === Preview / edit stage =========================================
  if (values) {
    return (
      <div className="space-y-3" data-testid={`rc-quick-preview-${kind}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-eyebrow flex items-center gap-1.5">
              <Sparkles className="w-3 h-3"/> Proposed Routing Context
            </div>
            <div className="text-xs text-white/50 mt-1">
              Review the extracted fields. Nothing saves until you confirm.
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={regenerate}
            className="text-white/60 hover:text-white hover:bg-white/10 flex-shrink-0"
            data-testid={`rc-quick-back-${kind}`}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5"/> Edit input
          </Button>
        </div>

        {fields.map(f => {
          const cur = (existing?.[f.key] || "").trim();
          const prop = (proposal?.[f.key] || "").trim();
          const changed = cur !== prop;
          return (
            <div key={f.key}>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`rcq-${kind}-${f.key}`}
                  className="text-[10px] uppercase tracking-wider text-white/40">
                  {f.label}
                </Label>
                {existing && changed && (
                  <span className="text-[10px] text-amber-300/80">updated</span>
                )}
              </div>
              <Textarea id={`rcq-${kind}-${f.key}`}
                rows={f.rows} value={values[f.key] || ""}
                onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                className="bg-white/5 border-white/10 text-sm mt-1"
                data-testid={`rc-quick-preview-input-${f.key}`}/>
              {existing && changed && cur && (
                <div className="text-[10px] text-white/40 mt-1 whitespace-pre-wrap leading-relaxed">
                  <span className="text-white/50">was:</span> {cur.length > 220 ? cur.slice(0, 220) + "…" : cur}
                </div>
              )}
            </div>
          );
        })}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm"
            onClick={() => { setValues(null); setProposal(null); setInputText(""); }}
            className="text-white/60 hover:text-white hover:bg-white/10"
            data-testid={`rc-quick-cancel-${kind}`}>
            <X className="w-3.5 h-3.5 mr-1.5"/> Cancel
          </Button>
          <Button variant="ghost" size="sm" onClick={analyze} disabled={analyzing}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid={`rc-quick-regenerate-${kind}`}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${analyzing ? "animate-spin" : ""}`}/>
            {analyzing ? "Analyzing…" : "Regenerate"}
          </Button>
          <Button size="sm" onClick={save} disabled={saving}
            className="rounded-full bg-white text-black hover:bg-white/90"
            data-testid={`rc-quick-save-${kind}`}>
            <Save className="w-3.5 h-3.5 mr-1.5"/> {saving ? "Saving…" : "Save context"}
          </Button>
        </div>
      </div>
    );
  }

  // === Capture stage ================================================
  return (
    <div className="space-y-3" data-testid={`rc-quick-capture-${kind}`}>
      {existing && (
        <div className="p-3 rounded-lg border border-white/10 bg-white/[0.03]">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
            Current context · v{existing.version}
          </div>
          <div className="text-xs text-white/60">
            The AI will consolidate the current context with your new input, not overwrite it.
          </div>
        </div>
      )}
      <div>
        <Label htmlFor={`rcq-input-${kind}`}
          className="text-[10px] uppercase tracking-wider text-white/40">
          {existing ? "Add / adjust context" : "Describe the context"}
        </Label>
        <Textarea id={`rcq-input-${kind}`}
          rows={7} value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={
            "Describe what this " + kind + " is, what matters to it, what should route here, and anything HQ should know."
          }
          className="bg-white/5 border-white/10 text-sm mt-1"
          data-testid={`rc-quick-input-${kind}`}/>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" onClick={analyze}
          disabled={analyzing || !inputText.trim()}
          className="rounded-full bg-white text-black hover:bg-white/90"
          data-testid={`rc-quick-analyze-${kind}`}>
          <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${analyzing ? "animate-spin" : ""}`}/>
          {analyzing ? "Analyzing…" : "Analyze"}
        </Button>
      </div>
      <div className="flex items-start gap-1.5 text-[11px] text-white/40">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0"/>
        <span>HQ extracts Purpose, Relevant Signals, Exclusions, Priorities, and Interpretation Rules. Nothing saves until you confirm the preview.</span>
      </div>
    </div>
  );
}
